import React, { useEffect, useState } from "react";
import "./AssistantDashboard.css";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getAuth, updatePassword } from "firebase/auth";
import { db } from "../../firebase";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";


export default function AssistantDashboard() {
  const auth = getAuth();
  const currentUser = auth.currentUser; // must be assistant

  /* profile & state */
  const [profile, setProfile] = useState(null);
  const [kidsParents, setKidsParents] = useState([]); // [{ kid, parent }]
  const [newPassword, setNewPassword] = useState("");
  const [feedback, setFeedback] = useState("");

    // Site analytics state
  const [siteAnalytics, setSiteAnalytics] = useState([]);

  // Fetch site analytics from Firestore
  useEffect(() => {
    async function fetchAnalytics() {
      const snapshot = await getDocs(collection(db, "siteMetrics"));
      const data = [];
      snapshot.forEach(doc => {
        data.push({
          date: doc.id,
          visits: doc.data().visits || 0,
        });
      });
      // Sort by date ascending
      data.sort((a, b) => a.date.localeCompare(b.date));
      setSiteAnalytics(data);
    }
    fetchAnalytics();
  }, []);

  /* load assistant profile */
  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "users", currentUser.uid);
    getDoc(ref).then((snap) => {
      if (snap.exists() && snap.data().accountType === "Assistant") {
        setProfile({ uid: snap.id, ...snap.data() });
      }
    });
  }, [currentUser]);

  /* build kid ↔ parent table */
  useEffect(() => {
    if (!profile) return;

    // listen to ALL kids
    const kidQ = query(
      collection(db, "users"),
      where("accountType", "==", "Kid")
    );
    const unsubKids = onSnapshot(kidQ, (kidSnap) => {
      const kidsArr = kidSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));

      if (kidsArr.length === 0) {
        setKidsParents([]);
        return;
      }

      // collect all parentCodes present among kids
      const codes = [...new Set(kidsArr.map((k) => k.parentCode))];
      if (codes.length === 0) {
        setKidsParents(kidsArr.map((k) => ({ kid: k, parent: null })));
        return;
      }

      // query parents with those codes
      const parentQ = query(
        collection(db, "users"),
        where("accountType", "==", "Parent"),
        where("parentCode", "in", codes.slice(0, 10)) // Firestore 'in' max 10
      );
      onSnapshot(parentQ, (parSnap) => {
        const parents = parSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        const pairs = kidsArr.map((kid) => ({
          kid,
          parent: parents.find((p) => p.parentCode === kid.parentCode) || null,
        }));
        setKidsParents(pairs);
      });
    });

    return unsubKids;
  }, [profile]);

  /* handle password */
  const changePassword = async () => {
    setFeedback("");
    if (!newPassword || newPassword.length < 6) {
      setFeedback("Password must be at least 6 characters.");
      return;
    }
    try {
      await updatePassword(currentUser, newPassword);
      setFeedback("Password updated successfully!");
      setNewPassword("");
    } catch (err) {
      console.error(err);
      setFeedback(err.message || "Error updating password");
    }
  };

  /* guards */
  if (!currentUser || !profile) {
    return (
      <div className="assistant-dashboard-page">
        <h2>You are not logged in or not an assistant.</h2>
        <p>Please log in with the Assistant account.</p>
      </div>
    );
  }

  /* UI */
  return (
    <div className="assistant-dashboard-page">
      <h2 className="assistant-dashboard-title">Assistant Dashboard</h2>
            <div className="assistant-dashboard-analytics">
        <h3>Site Visitors Analytics</h3>
        {siteAnalytics.length === 0 ? (
          <p>No analytics data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={siteAnalytics}>
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
              <Line type="monotone" dataKey="visits" stroke="#82ca9d" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        )}
        <div style={{ marginTop: 8, color: "#888" }}>
          <small>Chart: Daily visits (auto-aggregated by backend script). More metrics coming soon!</small>
        </div>
      </div>

      <div className="assistant-dashboard-container">
        {/* left profile */}
        <div className="assistant-profile-section">
          <h3>Assistant Profile</h3>
          <p><strong>Account Name:</strong> {profile.accountName}</p>
          <p><strong>First Name:</strong> {profile.firstName}</p>
          <p><strong>Last Name:</strong> {profile.lastName}</p>
          {profile.lastLoginTime && (
            <p><strong>Last Login:</strong> {new Date(profile.lastLoginTime).toLocaleString()}</p>
          )}

          <div className="assistant-password-section">
  <h3>Change Password</h3>
  <form
    onSubmit={e => {
      e.preventDefault();
      changePassword();
    }}
    autoComplete="off"
  >
    <input
  type="password"
  placeholder="Enter new password"
  value={newPassword}
  onChange={e => setNewPassword(e.target.value)}
  className="assistant-password-input"
  autoComplete="new-password"
/>
    <button type="submit" className="assistant-password-btn">
      Update Password
    </button>
      </form>
        {feedback && <p className="assistant-password-feedback">{feedback}</p>}
        </div>
        </div>

        {/* right kid/parent table */}
        <div className="assistant-accounts-section">
          <h3>Kids & Their Parents</h3>
          <table className="assistant-accounts-table">
            <thead>
              <tr>
                <th>Kid Account</th>
                <th>Kid Name</th>
                <th>Kid Last Login</th>
                <th>Parent Account</th>
                <th>Parent Name</th>
                <th>Parent Last Login</th>
              </tr>
            </thead>
            <tbody>
              {kidsParents.length === 0 && (
                <tr><td colSpan="6">No kids found.</td></tr>
              )}
              {kidsParents.map(({ kid, parent }) => (
                <tr key={kid.uid}>
                  <td>{kid.accountName}</td>
                  <td>{kid.firstName} {kid.lastName}</td>
                  <td>{kid.lastLoginTime ? (kid.lastLoginTime.toDate ? kid.lastLoginTime.toDate().toLocaleString() : new Date(kid.lastLoginTime).toLocaleString()) : "No login recorded"}</td>
                  <td>{parent?.accountName || "—"}</td>
                  <td>{parent ? `${parent.firstName} ${parent.lastName}` : "—"}</td>
                  <td>{parent?.lastLoginTime ? (parent.lastLoginTime.toDate ? parent.lastLoginTime.toDate().toLocaleString() : new Date(parent.lastLoginTime).toLocaleString()) : parent ? "No login recorded" : "—"}</td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
